import React, { useEffect, useState, useContext } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useUrlContext } from '../context/urlContext';
import { errorContext } from '../Components/ErrorDisplay';

const ALL_OFFER = { id: 0, name: 'All offers' };
const RESULT_OPTIONS = [
    { value: 0, label: 'All' },
    { value: 1, label: 'Success' },
    { value: 2, label: 'Failure' },
];

function ymd(date) {
    if (!date || Number.isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export default function RedemptionReport() {
    const { issuerId } = useParams();
    const { UrlHelperObj } = useUrlContext();
    const { handleError, notify } = useContext(errorContext);

    const today = new Date();
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(today.getDate() - 28);

    const [startDate, setStartDate] = useState(fourWeeksAgo);
    const [endDate, setEndDate] = useState(today);
    const [offers, setOffers] = useState([ALL_OFFER]);
    const [reportOfferId, setReportOfferId] = useState(0);
    const [reportResult, setReportResult] = useState(0);
    const [downloading, setDownloading] = useState(false);

    const datesValid = startDate && endDate && !Number.isNaN(startDate.getTime())
        && !Number.isNaN(endDate.getTime()) && startDate <= endDate;

    useEffect(() => {
        if (!issuerId) return;
        axios.get(UrlHelperObj.promotionUrl_GET(Number(issuerId), 1))
            .then(res => res.data)
            .then(rows => {
                const list = Array.isArray(rows)
                    ? rows.filter(p => Number(p.type) !== 0).map(p => ({ id: p.id, name: p.name }))
                    : [];
                setOffers([ALL_OFFER, ...list]);
            })
            .catch(err => handleError(err));
    }, [issuerId, UrlHelperObj, handleError]);

    async function handleDownloadReport() {
        if (!datesValid) {
            notify('Please choose a valid date range (start on or before end).', 'warning');
            return;
        }

        setDownloading(true);
        try {
            const params = {
                issuerId: Number(issuerId),
                startDate: ymd(startDate),
                endDate: ymd(endDate),
                offerId: Number(reportOfferId),
                result: Number(reportResult),
            };

            const res = await axios.get(UrlHelperObj.closedLoopEvidenceUrl(), { params, responseType: 'blob' });
            const blob = res.data;
            const contentType = res.headers?.['content-type'] || blob.type || '';
            const cd = res.headers?.['content-disposition'] || '';
            const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^"';]+)"?/i);
            const cdName = match ? decodeURIComponent(match[1]) : '';

            const isPdf = contentType.includes('pdf') || /\.pdf$/i.test(cdName);
            const isCsv = contentType.includes('csv') || /\.csv$/i.test(cdName);
            if (!isPdf && !isCsv) {
                let payload = {};
                try { payload = JSON.parse(await blob.text()); } catch (_) { /* not JSON */ }
                if (Number(payload.error) === 1) {
                    notify(payload.message || 'Report could not be generated.', 'error');
                } else {
                    notify(payload.message || 'No report data for the selected filters.', 'warning');
                }
                return;
            }

            const outType = isCsv ? 'text/csv' : 'application/pdf';
            const ext = isCsv ? 'csv' : 'pdf';
            const outBlob = blob.type ? blob : new Blob([blob], { type: outType });
            const fileName = cdName || `closed-loop-evidence_${ymd(startDate)}_${ymd(endDate)}.${ext}`;

            const objectUrl = URL.createObjectURL(outBlob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            handleError(err);
        } finally {
            setDownloading(false);
        }
    }

    const infoIcon = (definition) => (
        <Tooltip title={definition} arrow enterTouchDelay={0} leaveTouchDelay={4000}>
            <IconButton size="small" aria-label="metric definition" sx={{ p: 0.25, ml: 0.5 }}>
                <InfoOutlinedIcon fontSize="inherit" color="action" />
            </IconButton>
        </Tooltip>
    );

    return (
        <Box sx={{ p: 2 }}>
            <Paper sx={{ p: '8px 12px', mb: 2 }}>
                <Typography variant="h5" sx={{ textAlign: 'left' }}>Redemption Reports</Typography>
            </Paper>

            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Closed-Loop Redemption Evidence</Typography>
                    {infoIcon('Downloadable daily redemption report: member identifier, offer ID, outlet, timestamp, and redemption result. Uses the selected club and date range above plus the offer and result below.')}
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2} alignItems="center">
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid item xs={12} sm={6} md={3}>
                            <DatePicker
                                label="Start date"
                                value={startDate}
                                inputFormat="dd/MM/yyyy"
                                maxDate={endDate || undefined}
                                onChange={setStartDate}
                                renderInput={(params) => <TextField {...params} size="small" fullWidth InputLabelProps={{ shrink: true }} />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <DatePicker
                                label="End date"
                                value={endDate}
                                inputFormat="dd/MM/yyyy"
                                minDate={startDate || undefined}
                                onChange={setEndDate}
                                renderInput={(params) => <TextField {...params} size="small" fullWidth InputLabelProps={{ shrink: true }} />}
                            />
                        </Grid>
                    </LocalizationProvider>

                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label="Offer"
                            value={reportOfferId}
                            onChange={(e) => setReportOfferId(Number(e.target.value))}
                        >
                            {offers.map(o => (
                                <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label="Redemption result"
                            value={reportResult}
                            onChange={(e) => setReportResult(Number(e.target.value))}
                        >
                            {RESULT_OPTIONS.map(r => (
                                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleDownloadReport}
                        disabled={!datesValid || downloading}
                        startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {downloading ? 'Preparing…' : 'Download report'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
